import { Link, useNavigate } from "@tanstack/react-router";
import {
  Menu,
  X,
  UserCircle2,
  LogOut,
  ShieldCheck,
  LayoutDashboard,
  ChevronDown,
  Home,
  Building2,
  PartyPopper,
  TreePine,
  CalendarDays,
  Handshake,
} from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const links = [
  { to: "/", label: "Home", icon: Home },
  { to: "/hotels", label: "Hotels", icon: Building2 },
  { to: "/event-centres", label: "Event Centres", icon: PartyPopper },
  { to: "/open-spaces", label: "Open Spaces", icon: TreePine },
  { to: "/events", label: "Events", icon: CalendarDays },
] as const;

const SCROLL_RANGE = 120; // px over which we interpolate

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(0); // 0 floating → 1 sticky
  const { user, isAuthenticated, isAdmin, isVenueManager, isBusinessOwner, role, logout } = useAuth();
  const hasRole = !!role;
  const navigate = useNavigate();
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const p = Math.min(1, Math.max(0, y / SCROLL_RANGE));
      setProgress(p);
      if (headerRef.current) {
        headerRef.current.style.setProperty("--nav-progress", String(p));
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock body scroll when mobile drawer open
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      {/* Blurred Overlay backdrop */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-30 bg-black/15 dark:bg-black/45 backdrop-blur-[6px] transition-all duration-300 ease-out md:hidden"
        />
      )}
      <header ref={headerRef} className="fixed inset-x-0 top-0 z-50 navbar-header">
        <div className="navbar-inner mx-auto flex items-center justify-between gap-4 border px-4 transition-[background-color] duration-500 ease-out sm:px-6 motion-safe:animate-[navbar-in_0.7s_ease-out]">
          <Link to="/" className="flex items-center" onClick={() => setOpen(false)}>
            <span className="navbar-brand font-[family-name:var(--font-brand)] font-bold leading-none">
              Oyo<span className="text-primary italic">Booking</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-0.5 lg:flex" aria-label="Main navigation">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="navbar-link group relative rounded-full font-medium text-foreground/75 transition-colors hover:text-foreground [&.active]:text-primary"
                activeProps={{ className: "active" }}
                activeOptions={{ exact: l.to === "/" }}
                aria-label={l.label}
              >
                <span className="relative z-10">{l.label}</span>
                <span className="absolute inset-0 -z-0 scale-90 rounded-full bg-accent opacity-0 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100" />
              </Link>
            ))}
            {!isAuthenticated && (
              <Link
                to="/business-type"
                className="navbar-link group relative rounded-full font-medium text-primary transition-colors hover:text-primary/80"
              >
                <span className="relative z-10">Become a host</span>
                <span className="absolute inset-0 -z-0 scale-90 rounded-full bg-primary/10 opacity-0 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100" />
              </Link>
            )}
          </nav>

          <div className="navbar-actions hidden items-center lg:flex">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex max-w-[180px] items-center gap-2 rounded-full bg-accent px-3 py-1.5 text-sm font-medium transition-colors hover:bg-accent/70">
                    <UserCircle2 className="size-4 shrink-0" />
                    <span className="truncate">{user?.name}</span>
                    <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm font-semibold">{user?.name}</span>
                      <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link
                      to={hasRole ? (isVenueManager ? "/admin/listings" : "/admin") : "/dashboard"}
                      className="flex items-center gap-2"
                    >
                      {hasRole ? (
                        <ShieldCheck className="size-4" />
                      ) : (
                        <LayoutDashboard className="size-4" />
                      )}
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => {
                      logout();
                      navigate({ to: "/login" });
                    }}
                    className="text-destructive focus:text-destructive"
                  >
                    <LogOut className="size-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="whitespace-nowrap">
                    Sign in
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button variant="default" size="sm" className="rounded-full whitespace-nowrap">
                    Get started
                  </Button>
                </Link>
              </>
            )}
          </div>

          <button
            aria-label="Menu"
            className="grid size-10 place-items-center rounded-full hover:bg-accent lg:hidden"
            onClick={() => setOpen((s) => !s)}
          >
            {open ? <X className="size-5" /> : <Menu className="size-5" />}
          </button>
        </div>

        {/* Mobile slide-down drawer */}
        <div
          className={`fixed inset-x-0 top-0 z-40 mt-[110px] origin-top px-3 transition-all duration-500 ease-out lg:hidden ${
            open ? "scale-y-100 opacity-100" : "pointer-events-none scale-y-95 opacity-0"
          }`}
        >
          <div className="overflow-hidden rounded-2xl border border-border/60 bg-background/97 shadow-2xl backdrop-blur-2xl">
            {/* User header (when logged in) */}
            {isAuthenticated && (
              <div className="flex items-center gap-3 border-b border-border/50 bg-accent/40 px-5 py-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <UserCircle2 className="size-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-foreground">{user?.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </div>
            )}

            {/* Navigation links */}
            <nav className="flex flex-col px-2 py-2">
              {links.map((l) => {
                const Icon = l.icon;
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    activeProps={{ className: "active" }}
                    activeOptions={{ exact: l.to === "/" }}
                    className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium text-foreground/80 transition-colors hover:bg-accent hover:text-foreground [&.active]:bg-primary/8 [&.active]:text-primary"
                  >
                    <Icon className="size-[18px] shrink-0 text-muted-foreground transition-colors group-hover:text-foreground group-[.active]:text-primary" />
                    {l.label}
                  </Link>
                );
              })}
              {!isAuthenticated && (
                <Link
                  to="/business-type"
                  onClick={() => setOpen(false)}
                  className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-semibold text-primary transition-colors hover:bg-primary/8"
                >
                  <Handshake className="size-[18px] shrink-0" />
                  Become a host
                </Link>
              )}
            </nav>

            {/* Account section */}
            <div className="border-t border-border/50 px-2 py-2">
              {isAuthenticated ? (
                <>
                  {/* Dashboard link */}
                  <Link
                    to={hasRole ? (isVenueManager ? "/admin/listings" : "/admin") : "/dashboard"}
                    onClick={() => setOpen(false)}
                    className="group flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium text-foreground/80 transition-colors hover:bg-accent hover:text-foreground"
                  >
                    {hasRole ? (
                      <ShieldCheck className="size-[18px] shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
                    ) : (
                      <LayoutDashboard className="size-[18px] shrink-0 text-muted-foreground transition-colors group-hover:text-foreground" />
                    )}
                    Dashboard
                  </Link>

                  {/* Logout */}
                  <button
                    onClick={() => {
                      logout();
                      setOpen(false);
                      navigate({ to: "/login" });
                    }}
                    className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-medium text-destructive transition-colors hover:bg-destructive/8"
                  >
                    <LogOut className="size-[18px] shrink-0" />
                    Logout
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-2 p-2">
                  <Link to="/login" onClick={() => setOpen(false)}>
                    <Button variant="outline" className="w-full">
                      Sign in
                    </Button>
                  </Link>
                  <Link to="/signup" onClick={() => setOpen(false)}>
                    <Button className="w-full">Get started</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
